import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCampaigns, getEmailAccounts } from '../services/api';

function Dashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    totalRecipients: 0,
    sentEmails: 0,
    openedEmails: 0,
    respondedEmails: 0,
    totalAccounts: 0
  });

  useEffect(() => {
    loadData();
    
    const urlParams = new URLSearchParams(window.location.search);
    const successParam = urlParams.get('success');
    if (successParam === 'added') {
      setSuccess('Email account connected successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setSuccess(''), 5000);
    } else if (successParam === 'updated') {
      setSuccess('Email account updated successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setSuccess(''), 5000);
    }
  }, []);

  const loadData = async () => {
    try {
      const [campaignsRes, accountsRes] = await Promise.all([
        getCampaigns(),
        getEmailAccounts()
      ]);

      if (campaignsRes.success) {
        setCampaigns(campaignsRes.campaigns || []);
        calculateStats(campaignsRes.campaigns || []);
      }

      if (accountsRes.success) {
        setAccounts(accountsRes.accounts || []);
        setStats(prev => ({ ...prev, totalAccounts: accountsRes.accounts?.length || 0 }));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (campaignsData) => {
    const stats = {
      totalCampaigns: campaignsData.length,
      totalRecipients: 0,
      sentEmails: 0,
      openedEmails: 0,
      respondedEmails: 0
    };

    campaignsData.forEach(campaign => {
      stats.totalRecipients += parseInt(campaign.total_recipients || 0);
      stats.sentEmails += parseInt(campaign.sent_count || 0);
      stats.openedEmails += parseInt(campaign.opened_count || 0);
      stats.respondedEmails += parseInt(campaign.responded_count || 0);
    });

    setStats(prev => ({ ...prev, ...stats }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-white mb-2">Dashboard</h1>
          <p className="text-sm text-gray-400">Overview of your email campaigns and performance</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg backdrop-blur-sm">
            <div className="flex justify-between items-center">
              <p className="text-blue-400 text-sm">{success}</p>
              <button
                onClick={() => setSuccess('')}
                className="text-blue-400 hover:text-blue-300 text-xl leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <p className="text-xs text-gray-400 mb-1">Campaigns</p>
            <p className="text-2xl font-semibold text-white">{stats.totalCampaigns}</p>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <p className="text-xs text-gray-400 mb-1">Recipients</p>
            <p className="text-2xl font-semibold text-white">{stats.totalRecipients}</p>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <p className="text-xs text-gray-400 mb-1">Sent</p>
            <p className="text-2xl font-semibold text-white">{stats.sentEmails}</p>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <p className="text-xs text-gray-400 mb-1">Opened</p>
            <p className="text-2xl font-semibold text-white">{stats.openedEmails}</p>
          </div>
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <p className="text-xs text-gray-400 mb-1">Responses</p>
            <p className="text-2xl font-semibold text-white">{stats.respondedEmails}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/campaigns/create"
            className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm hover:border-blue-500/40 transition-all hover:shadow-lg hover:shadow-blue-600/10"
          >
            <h3 className="text-lg font-semibold text-white mb-2">Create Campaign</h3>
            <p className="text-sm text-gray-400">Set up a new email campaign</p>
          </Link>

          <Link
            to="/email-accounts"
            className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm hover:border-blue-500/40 transition-all hover:shadow-lg hover:shadow-blue-600/10"
          >
            <div className="text-3xl mb-3">⚙️</div>
            <h3 className="text-lg font-semibold text-white mb-2">Email Accounts</h3>
            <p className="text-sm text-gray-400">Manage your Gmail accounts</p>
          </Link>

          <Link
            to="/campaigns"
            className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm hover:border-blue-500/40 transition-all hover:shadow-lg hover:shadow-blue-600/10"
          >
            <h3 className="text-lg font-semibold text-white mb-2">View Campaigns</h3>
            <p className="text-sm text-gray-400">See all your campaigns</p>
          </Link>
        </div>

        {/* Recent Campaigns */}
        {campaigns.length > 0 && (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Recent Campaigns</h2>
              <Link
                to="/campaigns"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {campaigns.slice(0, 5).map((campaign) => (
                <Link
                  key={campaign.id}
                  to={`/campaigns/${campaign.id}`}
                  className="block p-4 border border-gray-700/50 rounded-lg hover:border-blue-500/40 hover:bg-[#0a0e27]/50 transition-all"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-medium text-white mb-1">
                        {campaign.campaign_name}
                      </h3>
                      <p className="text-xs text-gray-400">
                        {campaign.sent_count || 0} sent • {campaign.opened_count || 0} opened
                      </p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      campaign.status === 'completed' 
                        ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' 
                        : campaign.status === 'sending' 
                        ? 'bg-yellow-900/20 text-yellow-400 border border-yellow-500/30'
                        : 'bg-gray-700/50 text-gray-300 border border-gray-600/30'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
